import React from 'react'
import { Modal, Button, Typography, Switch, Alert } from 'antd'
import { Stack } from '@sureco/design-system'
import type { CustomerConfigurationInventoryItem } from '../hooks/useCustomerConfigurationInventory'
import useUpsertCustomerConfiguration from '../hooks/useUpsertCustomerConfiguration'

const { Title, Text } = Typography

interface ToggleFlagModalProps {
  open: boolean
  item: CustomerConfigurationInventoryItem | null
  customerId: string
  onClose: () => void
  onAfterClose: () => void
}

export default function ToggleFlagModal({
  open,
  item,
  customerId,
  onClose,
  onAfterClose,
}: ToggleFlagModalProps) {
  const { mutate, isPending, error, reset } = useUpsertCustomerConfiguration()

  const currentValue = item?.value === true
  const nextValue = !currentValue

  function handleConfirm() {
    if (!item) return
    mutate(
      { customerId, key: item.key, value: nextValue },
      {
        onSuccess: () => {
          reset()
          onClose()
        },
      }
    )
  }

  function handleCancel() {
    reset()
    onClose()
  }

  function handleAfterClose() {
    reset()
    onAfterClose()
  }

  const errorMessage =
    error instanceof Error ? error.message : error ? String(error) : null

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      centered
      width={480}
      afterClose={handleAfterClose}
    >
      {item && (
        <Stack space={20}>
          <Title level={4} style={{ marginBottom: 0 }}>
            {nextValue ? 'Enable' : 'Disable'} feature flag
          </Title>

          <div
            style={{
              background: '#F5FAFB',
              borderRadius: 8,
              padding: '16px 20px',
              border: '1px solid #E8F3F5',
            }}
          >
            <Stack space={8}>
              <Text strong style={{ fontSize: 15, color: '#103044' }}>
                {item.title}
              </Text>
              {item.description && (
                <Text style={{ fontSize: 13, color: '#5B7083' }}>
                  {item.description}
                </Text>
              )}
              <Text
                style={{
                  fontFamily: 'monospace',
                  fontSize: 12,
                  color: '#5B7083',
                }}
              >
                {item.key}
              </Text>
            </Stack>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text style={{ color: '#5B7083' }}>Current value:</Text>
            <Switch checked={currentValue} disabled />
            <Text style={{ color: '#5B7083' }}>→</Text>
            <Switch checked={nextValue} disabled />
            <Text style={{ color: '#103044', fontWeight: 600 }}>
              {nextValue ? 'Enabled' : 'Disabled'}
            </Text>
          </div>

          {errorMessage && (
            <Alert
              type="error"
              message="Failed to update flag"
              description={errorMessage}
              showIcon
            />
          )}

          <Stack space={8}>
            <Button
              type="primary"
              block
              loading={isPending}
              onClick={handleConfirm}
              danger={!nextValue}
            >
              {nextValue ? 'Enable flag' : 'Disable flag'}
            </Button>
            <Button block onClick={handleCancel} disabled={isPending}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      )}
    </Modal>
  )
}
